import React, { useMemo } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';
import { Users, UserPlus, Building2, Home, Layers, UserCheck } from 'lucide-react';

import { getAuthToken } from '../../services/auth';
import { StaffMemberCard } from './StaffMemberCard';
import { StaffFormModal } from './StaffFormModal';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export function StaffTab({
  staff,
  handleDeleteStaff,
  showStaffModal,
  setShowStaffModal,
  staffForm,
  setStaffForm,
  handleCreateStaff: parentCreateStaff,
  onReload
}) {
  const getToken = () => getAuthToken();

  const stats = useMemo(() => {
    const total = staff.length;
    // New: classification driven by explicit `staff_type` field.
    // "both" counts under BOTH office and resident totals.
    const both = staff.filter(s => (s.staff_type || '').toLowerCase() === 'both').length;
    const officeOnly = staff.filter(s => (s.staff_type || '').toLowerCase() === 'office_staff').length;
    const residentOnly = staff.filter(s => (s.staff_type || '').toLowerCase() === 'resident_staff').length;
    const unclassified = staff.filter(s => !s.staff_type).length;
    const officeStaff = officeOnly + both;
    const residentsStaff = residentOnly + both;
    const unassigned = staff.filter(s => !s.assigned_villas || s.assigned_villas.length === 0).length;
    const multiVilla = staff.filter(s => {
      const residentVillas = (s.assigned_villas || []).filter(v => !v.toLowerCase().includes('office'));
      return residentVillas.length > 1;
    }).length;
    return { total, officeStaff, residentsStaff, both, unclassified, unassigned, multiVilla };
  }, [staff]);

  const tiles = [
    { label: 'Total Staff', value: stats.total, color: 'bg-gk-navy-light', icon: Users },
    { label: 'Office Staff', value: stats.officeStaff, color: 'bg-blue-600', icon: Building2 },
    { label: 'Residents Staff', value: stats.residentsStaff, color: 'bg-emerald-600', icon: Home },
    { label: 'Office + Residents', value: stats.both, color: 'bg-amber-600', icon: Layers },
    { label: 'Unclassified', value: stats.unclassified, color: 'bg-slate-500', icon: UserCheck },
    { label: 'Multi-Villa', value: stats.multiVilla, color: 'bg-purple-600', icon: Users },
  ];

  const handleUploadFile = async (staffId, type) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = type === 'photo' ? 'image/*' : 'image/*,application/pdf';
    if (type === 'photo') input.capture = 'user';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const fd = new FormData();
      fd.append('file', file);
      try {
        const res = await fetch(`${API_URL}/staff/${staffId}/upload-${type}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${getToken()}` },
          body: fd
        });
        if (res.ok) {
          toast.success(`${type === 'photo' ? 'Photo' : 'ID document'} uploaded`);
          if (onReload) onReload();
        } else {
          const err = await res.json();
          toast.error(err.detail || 'Upload failed');
        }
      } catch { toast.error('Upload failed'); }
    };
    input.click();
  };

  const handleViewFile = async (staffId, type) => {
    try {
      const endpoint = type === 'photo' ? 'photo' : 'id-doc';
      const res = await fetch(`${API_URL}/staff/${staffId}/${endpoint}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch { toast.error('Failed to view file'); }
  };

  return (
    <>
      {/* Staff Summary Tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {tiles.map((tile) => (
          <Card key={tile.label} className="shadow-md border-0 overflow-hidden">
            <div className={`${tile.color} px-3 py-2`}>
              <div className="flex items-center gap-1.5">
                <tile.icon className="w-3.5 h-3.5 text-white/80" />
                <p className="text-[11px] font-medium text-white/90 leading-tight">{tile.label}</p>
              </div>
            </div>
            <CardContent className="p-3 pt-2">
              <p className="text-2xl font-bold text-slate-800" data-testid={`staff-stat-${tile.label.toLowerCase().replace(/\s+/g, '-')}`}>{tile.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="shadow-lg border-0">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Staff Management
          </CardTitle>
          <Button
            data-testid="register-staff-button"
            onClick={() => setShowStaffModal(true)}
            className="bg-gk-teal hover:bg-gk-teal-hover"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Register Staff
          </Button>
        </CardHeader>
        <CardContent>
          {staff.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No staff registered</p>
          ) : (
            <div className="space-y-3">
              {staff.map((member) => (
                <StaffMemberCard
                  key={member.id}
                  member={member}
                  onDelete={handleDeleteStaff}
                  onUploadFile={handleUploadFile}
                  onViewFile={handleViewFile}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <StaffFormModal
        open={showStaffModal}
        onOpenChange={setShowStaffModal}
        staffForm={staffForm}
        setStaffForm={setStaffForm}
        onReload={onReload}
      />
    </>
  );
}
